import determineTarget from './determine-target';
import setupFocusTrap from './focus-trap';
import toggleElement from './toggle-element';

const modalTriggers = new WeakMap();
const modalTriggerEvents = new WeakMap();

export default function setupModals( endpoints ) {
	const focusTraps = new WeakMap();

	// Initialize all dialog elements
	document.querySelectorAll( '.js-modal' ).forEach( modal => {
		modal.tabIndex = -1;
		modal.setAttribute( 'role', 'dialog' );
		modal.setAttribute( 'aria-hidden', true );

		const toggleTrap = setupFocusTrap( modal );
		focusTraps.set( modal, toggleTrap );
	} );

	function setuModalReset( name, value ) {
		const mediaQuery = window.matchMedia( value );

		function resetModals() {
			const modals = document.querySelectorAll( `.js-${ name }-modal` );

			if ( mediaQuery.matches ) {
				modals.forEach( modal => {
					modal.removeAttribute( 'tabindex' );
					modal.removeAttribute( 'role' );
					modal.setAttribute( 'aria-hidden', false );
					focusTraps.get( modal )?.( false );
				} );
			} else {
				modals.forEach( modal => {
					modal.tabIndex = -1;
					modal.setAttribute( 'role', 'dialog' );
					modal.setAttribute( 'aria-hidden', true );
					focusTraps.get( modal )?.( true );
				} );
			}
		}

		resetModals();
		if ( typeof mediaQuery.addEventListener === 'undefined' ) {
			mediaQuery.addListener( resetModals );
		} else {
			mediaQuery.addEventListener( 'change', resetModals );
		}

		return mediaQuery;
	}

	const modalEndpoints = endpoints.map( ( { name, value } ) => {
		return {
			name,
			mediaQuery: setuModalReset( name, value ),
		};
	} );

	// Handle opening the modal from a button that controls it
	function handleTrigger( event ) {
		const toggle = event.target.closest?.( '[aria-controls], [data-target]' );

		// Skip if mouseenter target is not the toggle
		if ( event.type === 'mouseenter' && toggle !== event.target ) {
			return;
		}

		// Skip if it doesn't control anything
		if ( ! toggle || ( ! toggle.hasAttribute( 'aria-controls' ) && ! toggle.dataset.target ) ) {
			return;
		}

		// Skip if the target isn't a dialog
		const target = determineTarget( toggle );
		if ( ! target || ! target.classList.contains( 'js-modal' ) ) {
			return;
		}

		// Skip if mouseenter but not hover modal
		if ( event.type === 'mouseenter' && ! target.classList.contains( 'js-hover-modal' ) ) {
			return;
		}

		// Check breakpoints
		const endpointMatch = modalEndpoints.find( ( { name, mediaQuery } ) => {
			return toggle.classList.contains( `js-${ name }-modal` ) && mediaQuery.matches;
		} );
		if ( endpointMatch ) {
			return;
		}

		event.preventDefault();

		if ( event.type === 'mouseenter' ) {
			// Just show the target
			toggleElement( target, true, false, event.type );

			// Record the event for mouseenter+click handling
			modalTriggerEvents.set( target, event.type );
		} else {
			// Show if hidden or a stackable modal, otherwise toggle
			const show = target.getAttribute( 'aria-hidden' ) === 'true' || target.classList.contains( 'js-modal--stackable' );

			// Skip if toggling-close but was opened via mouseenter
			if ( ! show && modalTriggerEvents.get( target ) === 'mouseenter' ) {
				return;
			}

			// Open and focus the dialog
			toggleElement( target, show, show, event.type );

			// Reset scroll position
			target.scrollTop = 0;

			// Record the trigger for refocusing later
			modalTriggers.set( target, toggle );

			if ( show ) {
				// Update open height for select animation usage
				target.style.setProperty( '--open-height', `${ target.scrollHeight }px` );
				// Record the event for mouseenter+click handling
				modalTriggerEvents.set( target, event.type );
			} else {
				modalTriggerEvents.delete( target );
			}
		}
	}

	document.querySelectorAll( '[aria-controls], [data-target]' ).forEach( trigger => {
		trigger.addEventListener( 'click', handleTrigger );
		trigger.addEventListener( 'mouseenter', handleTrigger );
	} );

	// Handle closing the modal, by clicking the close button or backdrop
	document.addEventListener( 'click', ( { target } ) => {
		if ( ! target.classList.contains( 'js-modal-close' ) && ! target.classList.contains( 'js-modal-backdrop' ) ) {
			return;
		}

		// Find the parent dialog
		const modal = target.closest( '.js-modal' );
		if ( ! modal ) {
			return;
		}

		// Close the dialog
		toggleElement( modal, false );

		// Refocus the trigger if found
		if ( modalTriggers.has( modal ) ) {
			modalTriggers.get( modal ).focus();
		}
	} );

	// Handle closing the modal via ESC key
	document.addEventListener( 'keyup', ( { key, target } ) => {
		if ( key !== 'Escape' ) {
			return;
		}

		// Find the parent dialog
		const modal = target.closest( '.js-modal' );
		if ( ! modal ) {
			return;
		}

		// Close the dialog
		toggleElement( modal, false );

		// Refocus the trigger if found
		if ( modalTriggers.has( modal ) ) {
			modalTriggers.get( modal ).focus();
		}
	} );
}
